import * as Sentry from '@sentry/react'
import {
  SentryFilterTagPaymentsButton,
  SentryFilterTagPaymentsDialog,
} from '@repo/sentry-utils/constants'

export const PAYMENTS_TURNSTILE_FEATURE_FLAG = 'payments-turnstile'

export type PaymentsTurnstileMutationName =
  | 'paymentsSubmitSubscriptionCheckout'
  | 'paymentsCreateSetupIntent'
  | 'paymentsExtendSubscription'

function collectErrorTexts(value: unknown): string[] {
  if (!value) {
    return []
  }

  if (typeof value === 'string') {
    return [value]
  }

  if (value instanceof Error) {
    return [value.message]
  }

  if (Array.isArray(value)) {
    return value.flatMap(collectErrorTexts)
  }

  if (typeof value !== 'object') {
    return []
  }

  const error = value as {
    code?: unknown
    error?: unknown
    errors?: unknown
    message?: unknown
  }
  const texts: string[] = []

  for (const field of [error.code, error.error, error.message]) {
    if (typeof field === 'string') {
      texts.push(field)
    }
  }

  return texts.concat(collectErrorTexts(error.errors))
}

function isTurnstileErrorText(text: string) {
  const normalized = text
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')

  return (
    normalized.includes('turnstile') ||
    normalized.includes('captcha') ||
    normalized.includes('cloudflare') ||
    normalized.includes('cf_turnstile') ||
    normalized.includes('bot_verification') ||
    normalized.includes('human_verification')
  )
}

export function getPaymentsTurnstileError(value: unknown) {
  const matchedText = collectErrorTexts(value).find(isTurnstileErrorText)

  if (!matchedText) {
    return null
  }

  if (value instanceof Error) {
    return value
  }

  return new Error(`Payments Turnstile validation failed: ${matchedText}`)
}

export function reportPaymentsTurnstileError({
  error,
  isPaymentButton,
  mutationName,
}: {
  error: unknown
  isPaymentButton: boolean
  mutationName: PaymentsTurnstileMutationName
}) {
  Sentry.captureException(error, {
    extra: {
      featureFlag: PAYMENTS_TURNSTILE_FEATURE_FLAG,
      mutation: mutationName,
    },
    tags: {
      component: isPaymentButton ? SentryFilterTagPaymentsButton : SentryFilterTagPaymentsDialog,
      feature: PAYMENTS_TURNSTILE_FEATURE_FLAG,
    },
  })
}
